import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { getApiBaseUrl } from '../../config/apiConfig';
import { FaUsers, FaSearch, FaBolt } from 'react-icons/fa';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi';

const STATUS_STYLES = {
  active: 'bg-green-50 text-green-700 border-green-200',
  expired: 'bg-red-50 text-red-700 border-red-200',
  trial: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
};

export default function SubscriptionsManager() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const token = Cookies.get('fbauthToken');
  const API = getApiBaseUrl();

  useEffect(() => {
    fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/v2/automation/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data;
      if (Array.isArray(data)) {
        setSubscriptions(data);
      } else if (data && typeof data === 'object') {
        setSubscriptions(Object.values(data));
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load subscriptions' });
    }
    setLoading(false);
  };

  const handleUpdateStatus = async (sub, newStatus) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/v2/automation/subscriptions`,
        { ...sub, status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSubscriptions((prev) =>
        prev.map((s) => (s.uid === sub.uid ? { ...s, status: newStatus } : s)),
      );
      setMessage({ type: 'success', text: 'Status updated' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update status' });
    }
    setSaving(false);
  };

  const filtered = subscriptions.filter((s) => {
    const matchSearch =
      !search ||
      (s.uid || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.ig_username || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus || s.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const counts = subscriptions.reduce(
    (acc, s) => {
      acc.total += 1;
      if (s.status === 'active') acc.active += 1;
      if (s.status === 'trial') acc.trial += 1;
      if (s.status === 'expired' || s.status === 'cancelled') acc.inactive += 1;
      return acc;
    },
    { total: 0, active: 0, trial: 0, inactive: 0 },
  );

  return (
    <div className="lg:p-1 min-h-screen lg:mr-[2vw] bg-gray-50">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold text-indigo-800 flex items-center gap-2">
            <FaUsers />
            <span>Automation Subscriptions</span>
          </h2>
          <p className="text-gray-600 mt-1">
            Manage user subscriptions to Instagram automation.
          </p>
        </div>
      </div>

      {/* Notice */}
      {message && (
        <div
          className={`mb-4 px-4 py-3 rounded-lg text-sm flex items-center gap-2 border ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          {message.type === 'success' ? <FiCheckCircle /> : <FiXCircle />}
          <span className="font-medium">{message.text}</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-3">
          <div className="bg-indigo-50 text-indigo-600 p-2.5 rounded-lg">
            <FaUsers />
          </div>
          <div>
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-bold text-gray-800">{counts.total}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-3">
          <div className="bg-green-50 text-green-600 p-2.5 rounded-lg">
            <FaBolt />
          </div>
          <div>
            <p className="text-sm text-gray-500">Active</p>
            <p className="text-2xl font-bold text-gray-800">{counts.active}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-3">
          <div className="bg-yellow-50 text-yellow-600 p-2.5 rounded-lg">
            <FaBolt />
          </div>
          <div>
            <p className="text-sm text-gray-500">Trial</p>
            <p className="text-2xl font-bold text-gray-800">{counts.trial}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-3">
          <div className="bg-gray-100 text-gray-600 p-2.5 rounded-lg">
            <FaBolt />
          </div>
          <div>
            <p className="text-sm text-gray-500">Inactive</p>
            <p className="text-2xl font-bold text-gray-800">{counts.inactive}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="flex items-center gap-2 flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500">
          <FaSearch className="text-gray-400" />
          <input
            className="flex-1 focus:outline-none text-sm bg-transparent"
            placeholder="Search by UID or username..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-w-[180px]"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Table card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <FaUsers />
            <span>Subscriptions</span>
          </h3>
          {loading && <span className="text-xs text-gray-500">Loading…</span>}
        </div>

        {!loading && filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <FaBolt className="mx-auto text-4xl mb-3 opacity-40" />
            <p>No subscriptions found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-xs text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-xs text-gray-500 uppercase tracking-wider">
                    IG Username
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-xs text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-xs text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-xs text-gray-500 uppercase tracking-wider">
                    End Date
                  </th>
                  <th className="text-right px-4 py-3 font-medium text-xs text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filtered.map((sub, idx) => (
                  <tr
                    key={sub.uid || idx}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-xs text-gray-500 break-all max-w-[220px]">
                      {sub.uid || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                          {(sub.ig_username || '?')[0].toUpperCase()}
                        </div>
                        <span className="font-medium text-gray-800">
                          @{sub.ig_username || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="capitalize px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full text-xs font-medium">
                        {sub.plan_id || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border capitalize ${
                          STATUS_STYLES[sub.status] || STATUS_STYLES.cancelled
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            sub.status === 'active'
                              ? 'bg-green-500'
                              : sub.status === 'trial'
                                ? 'bg-yellow-500'
                                : sub.status === 'expired'
                                  ? 'bg-red-500'
                                  : 'bg-gray-400'
                          }`}
                        />
                        {sub.status || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {sub.end_date
                        ? new Date(sub.end_date).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {sub.status === 'active' ? (
                        <button
                          onClick={() => handleUpdateStatus(sub, 'cancelled')}
                          disabled={saving}
                          type="button"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 border border-red-200 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUpdateStatus(sub, 'active')}
                          disabled={saving}
                          type="button"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 border border-green-200 px-3 py-1.5 rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50"
                        >
                          Activate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
